import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import {
  getAllSchoolService,
  createSchoolService,
  updateSchoolService,
  deleteSchoolService,
  getSchoolService,
  updateRestrictionService,
} from "@/service/school.service";
import {
  SchoolModel,
  SchoolQuery,
  SchoolRestrictionModel,
} from "@/types/school.model";
import type { AxiosError } from "axios";
import { ROLES, successToast } from "@/utils/constants";
import { fetchDashboardData } from "./dashboardSlice";

interface SchoolState {
  schools: SchoolModel[];
  currentSchool: SchoolModel | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  totalItems: number;
  noSchools: boolean;
  query: SchoolQuery;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

const initialState: SchoolState = {
  schools: [],
  noSchools: true,
  currentSchool: null,
  loading: true,
  actionLoading: false,
  error: null,
  totalItems: 0,
  query: {
    page: 1,
    pageSize: 10,
    search: "",
    fromDate: "",
    toDate: "",
    schoolId: "",
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
};

// Async Actions
export const fetchSchools = createAsyncThunk(
  "school/fetchAll",
  async (query: SchoolQuery, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const {
        data: { data, pageInfo },
      } = await getAllSchoolService(query);
      dispatch(setLoading(false));
      dispatch(setNoSchools(false));
      dispatch(setQuery(query));

      return {
        schools: data,
        pagination: {
          totalItems: pageInfo?.total_count as number,
          totalPages: pageInfo?.total_pages as number,
          currentPage: pageInfo?.current_page as number,
          pageSize: pageInfo?.per_page as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoSchools(true));
        throw error;
      }

      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch schools";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const fetchSchoolById = createAsyncThunk(
  "school/fetchById",
  async (id: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await getSchoolService(id);
      dispatch(setLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch school";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const createSchool = createAsyncThunk(
  "school/create",
  async (schoolData: Partial<SchoolModel>, { dispatch }) => {
    try {
      dispatch(setActionLoading(true));
      const response = await createSchoolService(schoolData as SchoolModel);

      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to create school";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const updateSchool = createAsyncThunk(
  "school/update",
  async (
    { id, schoolData }: { id: string; schoolData: Partial<SchoolModel> },
    { dispatch }
  ) => {
    try {
      dispatch(setActionLoading(true));
      // Ensure the school object has the id property
      const schoolToUpdate = { ...schoolData };
      const response = await updateSchoolService(
        id,
        schoolToUpdate as SchoolModel
      );

      dispatch(setActionLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to update school";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const deleteSchool = createAsyncThunk(
  "school/delete",
  async (id: string, { dispatch, getState }) => {
    try {
      dispatch(setActionLoading(true));
      await deleteSchoolService(id);

      successToast("School deleted successfully", "");

      const state = getState() as RootState;
      const userRole = state.auth.user?.role;

      const isSuperAdmin = userRole?.code === ROLES.SUPER_ADMIN;

      if (isSuperAdmin) {
        dispatch(fetchDashboardData());
      }

      dispatch(
        fetchSchools({
          page: state.schools.pagination.currentPage,
          pageSize: state.schools.pagination.pageSize,
        })
      );

      dispatch(setActionLoading(false));
      return id;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to delete school";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const updateRestriction = createAsyncThunk(
  "school/updateRestriction",
  async (restrictionData: SchoolRestrictionModel, { dispatch }) => {
    try {
      dispatch(setActionLoading(true));
      const response = await updateRestrictionService(restrictionData);

      successToast("School restriction updated successfully", "");

      dispatch(setActionLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message ||
        "Failed to update school restriction";
      dispatch(setError(message));
      throw error;
    }
  }
);

const schoolSlice = createSlice({
  name: "school",
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<SchoolQuery>) => {
      state.query = action.payload;
    },
    setNoSchools: (state, action: PayloadAction<boolean>) => {
      state.noSchools = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setActionLoading: (state, action: PayloadAction<boolean>) => {
      state.actionLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
      state.actionLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCurrentSchool: (state) => {
      state.currentSchool = null;
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch all schools
      .addCase(fetchSchools.fulfilled, (state, action) => {
        state.schools = action.payload.schools;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchSchools.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch schools";
      })

      // Fetch school by id
      .addCase(fetchSchoolById.fulfilled, (state, action) => {
        state.currentSchool = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchSchoolById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch school";
      })

      // Create school
      .addCase(createSchool.fulfilled, (state) => {
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(createSchool.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to create school";
      })

      // Update school
      .addCase(updateSchool.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(updateSchool.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to update school";
      })

      // Delete school
      .addCase(deleteSchool.fulfilled, (state) => {
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(deleteSchool.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to delete school";
      })

      // Update restriction
      .addCase(updateRestriction.fulfilled, (state) => {
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(updateRestriction.rejected, (state, action) => {
        state.actionLoading = false;
        state.error =
          action.error.message || "Failed to update school restriction";
      });
  },
});

export const {
  setLoading,
  setActionLoading,
  setError,
  clearError,
  resetCurrentSchool,
  resetAllState,
  setNoSchools,
  setQuery,
} = schoolSlice.actions;

// Selectors
export const selectSchools = (state: RootState) => state.schools.schools;
export const selectCurrentSchool = (state: RootState) =>
  state.schools.currentSchool;
export const selectSchoolLoading = (state: RootState) => state.schools.loading;
export const selectSchoolActionLoading = (state: RootState) =>
  state.schools.actionLoading;
export const selectSchoolError = (state: RootState) => state.schools.error;
export const selectTotalItems = (state: RootState) =>
  state.schools.pagination.totalItems;
export const selectNoSchools = (state: RootState) => state.schools.noSchools;
export const selectQuery = (state: RootState) => state.schools.query;

export default schoolSlice.reducer;
